import { useMemo, type ReactNode } from "react";
import { Pencil, Trash2, UserCheck, UserPlus, UserX } from "lucide-react";
import { Link } from "react-router-dom";
import type { GroupTotals, MemberPatch, MemberWithRatings } from "../../types";
import { DataGrid, type GridColumn } from "../DataGrid";
import { IconButton } from "../IconButton";
import { NotesPreview } from "../NotesPreview";
import { MemberTreeName } from "../MemberTreeName";
import { CheckboxInput } from "../fields/CheckboxInput";
import { EmptyState } from "../ScreenStates";
import { formatDate, memberDisplayName } from "../../utils/format";
import {
  orderMembersByColumn,
  orderMembersBySubscriber,
  type OrderedMemberRow,
} from "./memberOrdering";

interface MembersGridProps {
  groupId: string;
  members: readonly MemberWithRatings[];
  totals: GroupTotals | null;
  isLocked: boolean;
  lockedTitle?: string;
  isLoading: boolean;
  /** Shown instead of the add-member empty state when a quick filter leaves no rows. */
  filterEmptyMessage?: string;
  highlightedMemberId: string | null;
  onFlagChange: (
    memberId: string,
    patch: Pick<MemberPatch, "irixChecked" | "confirmedValidAuth">,
  ) => void;
  onEdit: (memberId: string) => void;
  onToggleExcluded: (member: MemberWithRatings, excluded: boolean) => void;
  onRemove: (member: MemberWithRatings) => void;
  children?: never;
}

function numberCell(value: number | null | undefined): ReactNode {
  return value === null || value === undefined ? (
    <span className="text-gray-400">–</span>
  ) : (
    value
  );
}

/**
 * Full-width members grid: alphabetical within the subscriber tree, compact rows,
 * two-line headers, no horizontal scroll at the 1400px container. Exclusion is
 * a row action (and a toggle on Edit Member); excluded rows render struck through.
 */
export function MembersGrid({
  groupId,
  members,
  totals,
  isLocked,
  lockedTitle,
  isLoading,
  filterEmptyMessage,
  highlightedMemberId,
  onFlagChange,
  onEdit,
  onToggleExcluded,
  onRemove,
}: MembersGridProps) {
  const rows = useMemo(() => orderMembersBySubscriber(members), [members]);

  const columns = useMemo<GridColumn<OrderedMemberRow>[]>(
    () => [
      {
        key: "name",
        header: "Name",
        sortValue: ({ member }) => `${member.lastName} ${member.firstName}`,
        render: ({ member, depth }) => (
          <MemberTreeName
            member={member}
            to={`/uw/groups/${groupId}/members/${member.id}`}
            isDependent={depth > 0}
          />
        ),
      },
      {
        key: "relation",
        header: "Relation",
        render: ({ member }) => member.relation,
      },
      {
        key: "sex",
        header: "Sex",
        sortValue: ({ member }) => member.gender,
        align: "center",
        render: ({ member }) =>
          member.gender ?? <span className="text-gray-400">–</span>,
      },
      {
        key: "dob",
        header: "DOB",
        sortValue: ({ member }) => member.dateOfBirth,
        render: ({ member }) =>
          formatDate(member.dateOfBirth) || (
            <span className="text-gray-400">–</span>
          ),
      },
      {
        key: "age",
        header: "Age",
        sortValue: ({ member }) => totals?.perMember[member.id]?.age ?? null,
        align: "right",
        render: ({ member }) => numberCell(totals?.perMember[member.id]?.age),
      },
      {
        key: "ft",
        header: "Ft",
        align: "right",
        render: ({ member }) => numberCell(member.heightFeet),
      },
      {
        key: "in",
        header: "In",
        align: "right",
        render: ({ member }) => numberCell(member.heightInches),
      },
      {
        key: "weight",
        header: "Wt",
        align: "right",
        render: ({ member }) => numberCell(member.weightLbs),
      },
      {
        key: "build",
        header: "Build",
        sortValue: ({ member }) => totals?.perMember[member.id]?.build ?? null,
        align: "right",
        render: ({ member }) => numberCell(totals?.perMember[member.id]?.build),
      },
      {
        key: "rx",
        header: "Rx\nDebits",
        sortValue: ({ member }) => totals?.perMember[member.id]?.rxDebits ?? null,
        align: "right",
        render: ({ member }) =>
          numberCell(totals?.perMember[member.id]?.rxDebits),
      },
      {
        key: "condition",
        header: "Cond.\nDebits",
        sortValue: ({ member }) => totals?.perMember[member.id]?.conditionDebits ?? null,
        align: "right",
        render: ({ member }) =>
          numberCell(totals?.perMember[member.id]?.conditionDebits),
      },
      {
        key: "total",
        header: "Total\nDebits",
        sortValue: ({ member }) => totals?.perMember[member.id]?.totalDebits ?? null,
        align: "right",
        render: ({ member }) => (
          <strong>
            {numberCell(totals?.perMember[member.id]?.totalDebits)}
          </strong>
        ),
      },
      {
        key: "irix",
        header: "IRIX\nChecked",
        sortValue: ({ member }) => Number(member.irixChecked),
        align: "center",
        render: ({ member }) => (
          <CheckboxInput
            id={`irix-${member.id}`}
            label={`IRIX Checked: ${memberDisplayName(member)}`}
            hideLabel
            checked={member.irixChecked}
            disabled={isLocked}
            onChange={(irixChecked) => onFlagChange(member.id, { irixChecked })}
          />
        ),
      },
      {
        key: "auth",
        header: "Signed\nAuth",
        sortValue: ({ member }) => Number(member.confirmedValidAuth),
        align: "center",
        render: ({ member }) => (
          <CheckboxInput
            id={`auth-${member.id}`}
            label={`Signed Auth: ${memberDisplayName(member)}`}
            hideLabel
            checked={member.confirmedValidAuth}
            disabled={isLocked}
            onChange={(confirmedValidAuth) =>
              onFlagChange(member.id, { confirmedValidAuth })
            }
          />
        ),
      },
      {
        key: "notes",
        header: "Notes",
        align: "center",
        render: ({ member }) =>
          member.notes ? (
            <NotesPreview
              notes={member.notes}
              label={`Notes for ${memberDisplayName(member)}`}
            />
          ) : (
            <span className="sr-only">No notes</span>
          ),
      },
    ],
    [groupId, totals, isLocked, onFlagChange],
  );

  return (
    <DataGrid
      caption="Group members"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.member.id}
      defaultSort={{ key: "name", direction: "asc" }}
      sortRows={(current, sort, sortValue) =>
        orderMembersByColumn(
          current.map((row) => row.member),
          sort,
          (member) => sortValue({ member, depth: 0 }),
        )
      }
      isLoading={isLoading}
      density="compact"
      rowDepth={(row) => row.depth}
      isRowExcluded={(row) => row.member.isExcluded}
      rowClassName={(row) =>
        row.member.id === highlightedMemberId ? "row-highlight" : ""
      }
      actionsHeader="Member actions"
      renderActions={({ member }) => (
        <span className="inline-flex">
          <IconButton
            icon={Pencil}
            label={`Edit ${memberDisplayName(member)}`}
            disabled={isLocked}
            title={lockedTitle}
            onClick={() => onEdit(member.id)}
            size="sm"
          />
          <IconButton
            icon={member.isExcluded ? UserCheck : UserX}
            label={`${member.isExcluded ? "Include" : "Exclude"} ${memberDisplayName(member)}`}
            disabled={isLocked}
            title={
              lockedTitle ??
              (member.isExcluded
                ? "Bring this member back into the counts, totals, and RMM"
                : "Keep this member's ratings but drop them from every count, total, and RMM")
            }
            onClick={() => onToggleExcluded(member, !member.isExcluded)}
            size="sm"
          />
          <IconButton
            icon={Trash2}
            label={`Remove ${memberDisplayName(member)}`}
            tone="danger"
            disabled={isLocked}
            title={lockedTitle}
            onClick={() => onRemove(member)}
            size="sm"
          />
        </span>
      )}
      emptyState={
        filterEmptyMessage ? (
          <EmptyState icon={UserPlus} message={filterEmptyMessage} />
        ) : (
          <EmptyState
            icon={UserPlus}
            message="No members yet. Add a member or import a census."
            action={
              !isLocked ? (
                <Link
                  to={`/uw/groups/${groupId}/members/new`}
                  className="inline-flex min-h-[44px] items-center rounded-lg bg-brand-green px-4 py-2 text-sm font-medium text-white hover:bg-brand-green-hover focus:outline-none focus:ring-2 focus:ring-brand-green-50"
                >
                  Add New Member
                </Link>
              ) : undefined
            }
          />
        )
      }
    />
  );
}
